import { Assets, Rectangle, Texture } from 'pixi.js';
import type { EnemyDef } from '../core/types';

/**
 * 1.22 적 도트 그림 — public/assets/enemies/<art>.json (tools/slice_enemies.py 가 받은 요괴 시트에서 굽는다).
 *
 * 한 장마다 발 위치(ax, ay)가 있어 걷다 몸을 뻗어도 발이 제자리다. 맞을 때 번쩍이는 흰 그림(flash)은 읽을 때 한 번 만든다
 * (곱하기 색으로는 밝게 못 만들어서, 예전 도트 적은 맞아도 번쩍임이 안 보였다).
 * 이펙트(fx.json)는 적 탄·경고·보스 기술 그림 — add 는 더하기 합성으로 그린다(하트·장풍·칼빛).
 */

export interface EFrame {
  tex: Texture;
  /** 흰 실루엣 — 맞을 때 · 엘리트 금빛 덧칠 */
  flash: Texture;
  /** 발 위치 (0~1) */
  ax: number;
  ay: number;
  w: number;
  h: number;
}

export interface EnemyArt {
  id: string;
  /** 그림이 보는 쪽. none 이면 뒤집지 않는다(정면) */
  face: 'left' | 'right' | 'none';
  /** 동작마다 보는 쪽이 다르면 (털괴: 걷기만 왼쪽) */
  faces: Record<string, 'left' | 'right' | 'none'>;
  anims: Map<string, EFrame[]>;
  fps: Record<string, number>;
  /** 걷는 그림 높이(px) — 판정 원의 가운데가 몸 가운데에 오게 발을 내린다 */
  moveH: number;
  /** 걷기 줄이 여럿이면(미물) 마리마다 하나를 고른다 */
  moves: string[];
}

export interface FxArt {
  frames: Texture[];
  w: number[];
  h: number[];
  add: boolean;
}

type FrameJson = { x: number; y: number; w: number; h: number; ax?: number; ay?: number };

const arts = new Map<string, EnemyArt>();
const fx = new Map<string, FxArt>();

export function enemyArtOf(id: string | undefined): EnemyArt | null {
  return id ? arts.get(id) ?? null : null;
}

/** 이 동작을 face 쪽(1 오른쪽 · -1 왼쪽)으로 보게 하려면 뒤집어야 하나. 정면 그림(none)은 보스만 뒤집는다(공손찬 초승달) */
export function artFlip(art: EnemyArt, anim: string, face: number, boss: boolean): boolean {
  const f = art.faces[anim] ?? art.face;
  return f === 'left' ? face > 0 : f === 'right' ? face < 0 : boss && face < 0;
}

export function fxArt(name: string): FxArt | null {
  return fx.get(name) ?? null;
}

/** 불투명한 곳을 전부 흰색으로 — 맞을 때 번쩍임. 이미 읽은 그림(ImageBitmap 등)을 그대로 쓴다(두 번 받지 않게) */
function whiteOf(base: Texture): Texture {
  const src = base.source;
  const c = document.createElement('canvas');
  c.width = src.pixelWidth;
  c.height = src.pixelHeight;
  const ctx = c.getContext('2d')!;
  ctx.drawImage(src.resource as CanvasImageSource, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, c.width, c.height);
  const tex = Texture.from(c);
  tex.source.scaleMode = 'nearest';
  tex.source.autoGenerateMipmaps = false;
  return tex;
}

async function loadOne(id: string): Promise<void> {
  const res = await fetch(`assets/enemies/${id}.json`);
  if (!res.ok) throw new Error(`${id}.json (${res.status})`);
  const meta = (await res.json()) as {
    image: string;
    face: EnemyArt['face'];
    faces?: EnemyArt['faces'];
    frames: FrameJson[];
    anims: Record<string, number[]>;
    fps: Record<string, number>;
  };
  const url = `assets/enemies/${meta.image}`;
  const base = (await Assets.load(url)) as Texture;
  base.source.scaleMode = 'nearest';
  base.source.autoGenerateMipmaps = false;
  const white = whiteOf(base);
  const frames = meta.frames.map((f) => {
    const r = new Rectangle(f.x, f.y, f.w, f.h);
    return {
      tex: new Texture({ source: base.source, frame: r }),
      flash: new Texture({ source: white.source, frame: r.clone() }),
      ax: (f.ax ?? f.w / 2) / f.w,
      ay: (f.ay ?? f.h) / f.h,
      w: f.w,
      h: f.h,
    };
  });
  const anims = new Map<string, EFrame[]>();
  for (const [name, ids] of Object.entries(meta.anims)) {
    const list = ids.map((i) => frames[i]).filter(Boolean);
    if (list.length) anims.set(name, list);
  }
  const moves = [...anims.keys()].filter((k) => k === 'move' || k.startsWith('move_'));
  const first = anims.get(moves[0] ?? 'idle') ?? frames;
  const hs = first.map((f) => f.h).sort((a, b) => a - b);
  arts.set(id, { id, face: meta.face, faces: meta.faces ?? {}, anims, fps: meta.fps, moveH: hs[hs.length >> 1] ?? 40, moves: moves.length ? moves : ['idle'] });
}

/** 이펙트 아틀라스 하나 — 적 것(assets/enemies/fx)과 1.24 전격 질주 대시 번개(assets/fx/zap)가 같은 이름 자리를 쓴다 */
async function loadFx(dir = 'assets/enemies', file = 'fx.json'): Promise<void> {
  const res = await fetch(`${dir}/${file}`);
  if (!res.ok) throw new Error(`${file} (${res.status})`);
  const meta = (await res.json()) as { image: string; frames: FrameJson[]; anims: Record<string, number[]>; modes: Record<string, string> };
  const base = (await Assets.load(`${dir}/${meta.image}`)) as Texture;
  base.source.scaleMode = 'nearest';
  base.source.autoGenerateMipmaps = false;
  for (const [name, ids] of Object.entries(meta.anims)) {
    const fr = ids.map((i) => meta.frames[i]);
    fx.set(name, {
      frames: fr.map((f) => new Texture({ source: base.source, frame: new Rectangle(f.x, f.y, f.w, f.h) })),
      w: fr.map((f) => f.w),
      h: fr.map((f) => f.h),
      add: meta.modes[name] === 'add',
    });
  }
}

/**
 * 적 그림을 읽는다. 못 읽은 적은 예전처럼 코드로 찍은 덩어리로 나온다(경고만).
 * 보스 그림(크다)은 기다리지 않고 뒤에서 읽는다 — 첫 보스는 4분에 나온다
 */
export async function loadEnemyArt(defs: readonly EnemyDef[]): Promise<void> {
  const load = (list: readonly EnemyDef[]): Promise<unknown> =>
    Promise.all(
      [...new Set(list.map((d) => d.art).filter((a): a is string => !!a))].map((id) =>
        loadOne(id).catch((e) => {
          console.warn(`[art] 적 그림 ${id} 을 못 읽었다. 도형으로 대신한다.`, e);
        }),
      ),
    );
  await Promise.all([
    load(defs.filter((d) => !d.boss)),
    loadFx().catch((e) => {
      console.warn('[art] 적 이펙트 그림을 못 읽었다. 둥근 탄으로 대신한다.', e);
    }),
    // 1.24 대시 번개 (tools/build_fx.py --zap)
    loadFx('assets/fx', 'zap.json').catch((e) => {
      console.warn('[art] 대시 번개 그림을 못 읽었다. 대시는 그림 없이 나간다.', e);
    }),
  ]);
  void load(defs.filter((d) => d.boss));
}
